import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

const MAX_INCLUDED_REGION_CODES = 15;
const GOOGLE_PLACES_LOAD_TIMEOUT_MS = 5000;
const EMPTY_COUNTRIES = [];

function isGooglePlacesReady() {
  if (typeof window === 'undefined') return false;
  return Boolean(window.google?.maps?.places?.AutocompleteSuggestion?.fetchAutocompleteSuggestions && window.google?.maps?.Geocoder);
}

function toRegionCodeList(countries) {
  if (!Array.isArray(countries)) return [];

  return [
    ...new Set(
      countries
        .map((c) => (typeof c === 'string' ? c : c?.code))
        .filter(Boolean)
        .map((code) => String(code).trim().toLowerCase())
        .filter((code) => code.length >= 2 && code.length <= 3),
    ),
  ];
}

function normalizeIncludedRegionCodes(allowedCountries, priorityCountries = []) {
  const allowed = toRegionCodeList(allowedCountries);
  if (allowed.length === 0) return [];

  const priority = toRegionCodeList(priorityCountries);
  return [...new Set([...priority, ...allowed])].slice(0, MAX_INCLUDED_REGION_CODES);
}

function extractAddressComponentsFromGeocodeResult(result) {
  const componentsList = result?.address_components || [];
  const pickFirst = (typeNames) => {
    for (let i = 0; i < typeNames.length; i += 1) {
      const t = typeNames[i];
      const match = componentsList.find((c) => c.types.includes(t));
      if (match) return match;
    }
    return null;
  };

  const city = pickFirst(['locality', 'sublocality', 'sublocality_level_1', 'postal_town', 'administrative_area_level_2'])?.long_name || '';
  const state = pickFirst(['administrative_area_level_1'])?.long_name || '';
  const postalCode = pickFirst(['postal_code'])?.long_name || '';
  const country = pickFirst(['country'])?.short_name?.toLowerCase() || '';
  const route = pickFirst(['route'])?.long_name || '';
  const streetNumber = pickFirst(['street_number'])?.long_name || '';

  return { city, state, postalCode, country, route, streetNumber };
}

function NewAddressAutocomplete({
  value,
  onChange,
  onAddressSelect,
  placeholder,
  isInvalid,
  error,
  label,
  required = false,
  className = '',
  backfillWhenIncomplete = false,
  addressParts = null,
  isolateSearchInput = false,
  includedPrimaryTypes = null,
  showSearchIcon = true,
  helperText = 'Select a suggestion to prefill the fields, or enter the address manually.',
  allowedCountries: allowedCountriesProp,
  priorityCountries: priorityCountriesProp,
  ...props
}) {
  const allowedCountries = allowedCountriesProp ?? EMPTY_COUNTRIES;
  const priorityCountries = priorityCountriesProp ?? EMPTY_COUNTRIES;

  const containerRef = useRef(null);
  const [input, setInput] = useState(value || '');
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [placesUnavailable, setPlacesUnavailable] = useState(false);
  const [placesReadyTick, setPlacesReadyTick] = useState(0);
  const [activeSuggestion, setActiveSuggestion] = useState(-1);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const isSelectingRef = useRef(false);
  const isMouseDownOnSuggestionsRef = useRef(false);
  const lastQueryRef = useRef('');
  const onAddressSelectRef = useRef(onAddressSelect);
  const geocodeBackfillDoneKeyRef = useRef('');
  const sessionTokenRef = useRef(null);
  const hasInteractedRef = useRef(false);
  const allowedCountriesRef = useRef(allowedCountries);
  const priorityCountriesRef = useRef(priorityCountries);
  const includedPrimaryTypesRef = useRef(includedPrimaryTypes);

  onAddressSelectRef.current = onAddressSelect;
  hasInteractedRef.current = hasInteracted;
  allowedCountriesRef.current = allowedCountries;
  priorityCountriesRef.current = priorityCountries;
  includedPrimaryTypesRef.current = includedPrimaryTypes;

  const getSessionToken = () => {
    if (!sessionTokenRef.current && window.google?.maps?.places?.AutocompleteSessionToken) {
      sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
    }
    return sessionTokenRef.current;
  };

  const debouncedSearchRef = useRef(null);
  if (!debouncedSearchRef.current) {
    debouncedSearchRef.current = debounce(async (searchInput) => {
      if (isSelectingRef.current) return;

      if (!hasInteractedRef.current || !searchInput || searchInput.length < 2) {
        setSuggestions([]);
        setLoading(false);
        setPlacesUnavailable(false);
        return;
      }

      if (!isGooglePlacesReady()) {
        setSuggestions([]);
        setLoading(false);
        setPlacesUnavailable(true);
        return;
      }

      lastQueryRef.current = searchInput;
      setLoading(true);
      setPlacesUnavailable(false);

      try {
        const sessionToken = getSessionToken();
        const request = { input: searchInput };
        if (sessionToken) request.sessionToken = sessionToken;
        const primaryTypes = includedPrimaryTypesRef.current;
        if (Array.isArray(primaryTypes) && primaryTypes.length > 0) {
          request.includedPrimaryTypes = primaryTypes;
        }
        const regionCodes = normalizeIncludedRegionCodes(allowedCountriesRef.current, priorityCountriesRef.current);
        if (regionCodes.length > 0) {
          const [region] = regionCodes;
          request.includedRegionCodes = regionCodes;
          request.region = region;
        }

        const { suggestions: rawSuggestions } = await window.google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(request);

        if (isSelectingRef.current) {
          setLoading(false);
          return;
        }

        if (lastQueryRef.current !== searchInput) {
          setLoading(false);
          return;
        }

        setSuggestions((rawSuggestions || []).map((s) => s.placePrediction));
      } catch {
        setSuggestions([]);
        setPlacesUnavailable(true);
      } finally {
        if (!isSelectingRef.current) setLoading(false);
      }
    }, 300);
  }

  useEffect(() => {
    debouncedSearchRef.current(input);
  }, [input, placesReadyTick]);

  useEffect(() => {
    if (isGooglePlacesReady()) {
      setPlacesUnavailable(false);
      return undefined;
    }

    const startedAt = Date.now();
    const intervalId = window.setInterval(() => {
      if (isGooglePlacesReady()) {
        setPlacesUnavailable(false);
        setPlacesReadyTick((tick) => tick + 1);
        window.clearInterval(intervalId);
        return;
      }

      if (Date.now() - startedAt >= GOOGLE_PLACES_LOAD_TIMEOUT_MS) {
        window.clearInterval(intervalId);
      }
    }, 250);

    return () => window.clearInterval(intervalId);
  }, []);

  useEffect(() => {
    if (!backfillWhenIncomplete) return;
    const cb = onAddressSelectRef.current;
    if (!cb) return;
    const ap = addressParts || {};
    const line = String(ap.line1 || '').trim();
    const pc = String(ap.postCode || '').trim();
    const ctry = String(ap.country || '').trim();
    const hasCity = String(ap.city || '').trim();
    const hasState = String(ap.stateRegionProvince || '').trim();
    if (hasCity && hasState) return;
    if (!line || !ctry) return;
    if (!window.google?.maps?.Geocoder) return;

    const baseKey = `${line}|${pc}|${ctry}`;
    if (geocodeBackfillDoneKeyRef.current === baseKey) return;

    const geocoder = new window.google.maps.Geocoder();
    const query = [line, pc, ctry].filter(Boolean).join(', ');

    geocoder
      .geocode({ address: query })
      .then(({ results }) => {
        if (!results?.length) return;
        geocodeBackfillDoneKeyRef.current = baseKey;
        const result = results[0];
        const latLng = {
          lat: result.geometry.location.lat(),
          lng: result.geometry.location.lng(),
        };
        const components = extractAddressComponentsFromGeocodeResult(result);
        cb({ fullAddress: result.formatted_address || query, latLng, components, source: 'backfill' });
      })
      .catch(() => {});
  }, [backfillWhenIncomplete, addressParts?.line1, addressParts?.postCode, addressParts?.country, addressParts?.city, addressParts?.stateRegionProvince]);

  useEffect(() => {
    setInput(value || '');
  }, [value]);

  useEffect(() => {
    if (!isFocused) return undefined;

    const onDocMouseDown = (e) => {
      const el = containerRef.current;
      if (!el) return;
      if (el.contains(e.target)) return;
      if (isSelectingRef.current) return;
      setIsFocused(false);
      setSuggestions([]);
      setActiveSuggestion(-1);
      setLoading(false);
      setPlacesUnavailable(false);
    };

    document.addEventListener('mousedown', onDocMouseDown, true);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown, true);
    };
  }, [isFocused]);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    isSelectingRef.current = false;
    setHasInteracted(true);
    hasInteractedRef.current = true;
    setPlacesUnavailable(false);
    setActiveSuggestion(-1);
    setInput(newValue);
    if (!isolateSearchInput) {
      onChange(newValue);
    }
  };

  const handleInputFocus = () => {
    setHasInteracted(true);
    hasInteractedRef.current = true;
    setIsFocused(true);
  };

  const handleInputBlur = () => {
    setTimeout(() => {
      if (isMouseDownOnSuggestionsRef.current) return;
      if (isSelectingRef.current) return;
      setIsFocused(false);
      setSuggestions([]);
      setActiveSuggestion(-1);
      setLoading(false);
      setPlacesUnavailable(false);
    }, 0);
  };

  const handleSuggestionClick = async (suggestion) => {
    isSelectingRef.current = true;

    const addressText = suggestion.text?.text || '';

    setSuggestions([]);
    setPlacesUnavailable(false);
    setActiveSuggestion(-1);
    setLoading(false);
    setInput(addressText);
    if (!isolateSearchInput) {
      onChange(addressText);
    }
    sessionTokenRef.current = null;

    if (onAddressSelect) {
      try {
        const geocoder = new window.google.maps.Geocoder();
        const { results } = await geocoder.geocode({ placeId: suggestion.placeId });
        if (results && results.length > 0) {
          const result = results[0];
          const latLng = {
            lat: result.geometry.location.lat(),
            lng: result.geometry.location.lng(),
          };
          const components = extractAddressComponentsFromGeocodeResult(result);
          onAddressSelect({ fullAddress: addressText, latLng, components, source: 'selection' });
        } else {
          onAddressSelect({ fullAddress: addressText, source: 'selection' });
        }
      } catch {
        onAddressSelect({ fullAddress: addressText, source: 'selection' });
      } finally {
        setTimeout(() => {
          isSelectingRef.current = false;
        }, 500);
      }
    } else {
      setTimeout(() => {
        isSelectingRef.current = false;
      }, 500);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      if (suggestions.length === 0) return;
      const index = activeSuggestion >= 0 ? activeSuggestion : 0;
      if (index < suggestions.length) {
        handleSuggestionClick(suggestions[index]);
      }
      return;
    }

    if (suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setActiveSuggestion((prev) => (prev < suggestions.length - 1 ? prev + 1 : prev));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setActiveSuggestion((prev) => (prev > 0 ? prev - 1 : prev));
        break;
      case 'Escape':
        setSuggestions([]);
        setActiveSuggestion(-1);
        break;
      default:
        break;
    }
  };

  return (
    <div ref={containerRef} className={className}>
      {label && (
        // eslint-disable-next-line jsx-a11y/label-has-associated-control
        <label className='text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70'>
          {label} {required && '*'}
        </label>
      )}
      <div className='relative'>
        <div className='relative'>
          {showSearchIcon ? (
            <div className='pointer-events-none absolute inset-y-0 left-3 z-[1] flex items-center text-[#7A9AB0]'>
              <svg width='18' height='18' viewBox='0 0 24 24' fill='none' aria-hidden>
                <path
                  d='M10.5 18a7.5 7.5 0 1 1 0-15 7.5 7.5 0 0 1 0 15Z'
                  stroke='currentColor'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
                <path d='M21 21l-4.35-4.35' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
              </svg>
            </div>
          ) : null}
          <input
            type='text'
            {...props}
            value={input}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            placeholder={placeholder || 'Search for an address'}
            className={`flex h-10 w-full rounded-md border ${isInvalid ? 'border-red-500' : 'border-input'} bg-background ${
              showSearchIcon ? 'pl-10 pr-3' : 'px-3'
            } py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2`}
            autoComplete='off'
          />
        </div>
        {helperText ? <p className='mt-2 text-sm text-muted-foreground'>{helperText}</p> : null}
        {placesUnavailable && hasInteracted && input.length >= 2 ? (
          <p className='mt-2 text-[11.5px] leading-5 text-[#A46A00]'>
            Address suggestions are unavailable right now. You can fill in the address manually below.
          </p>
        ) : null}
        {error && <p className='text-sm text-red-500 mt-1'>{error}</p>}

        {isFocused && (suggestions.length > 0 || loading) && (
          <div
            className='absolute left-0 right-0 z-50 mt-1 w-full overflow-hidden rounded-md border border-gray-300 bg-white text-left shadow-lg'
            style={{ zIndex: 1000 }}
          >
            {loading && suggestions.length === 0 ? (
              <div className='px-2.5 py-1.5 text-xs text-gray-600'>
                <div className='d-flex align-items-center'>
                  <div className='spinner-border spinner-border-sm me-2' role='status'>
                    <span className='visually-hidden'>Loading...</span>
                  </div>
                  Loading addresses...
                </div>
              </div>
            ) : null}
            {suggestions.map((suggestion, index) => {
              const isActive = index === activeSuggestion;
              const suggestionClassName = [
                'flex cursor-pointer border-b border-gray-100 px-2.5 py-1.5 text-left last:border-b-0',
                isActive ? 'bg-blue-50 text-blue-800' : 'hover:bg-gray-50',
              ].join(' ');

              const mainText = suggestion.mainText?.text || suggestion.text?.text || '';
              const secondaryText = suggestion.secondaryText?.text || '';

              return (
                <div
                  key={suggestion.placeId}
                  className={suggestionClassName}
                  role='button'
                  tabIndex={0}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    isMouseDownOnSuggestionsRef.current = true;
                    handleSuggestionClick(suggestion);
                  }}
                  onClick={() => handleSuggestionClick(suggestion)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleSuggestionClick(suggestion);
                    }
                  }}
                  onMouseEnter={() => setActiveSuggestion(index)}
                >
                  <i className='bi bi-geo-alt mr-2 mt-0.5 shrink-0 text-xs text-gray-400' aria-hidden />
                  <div className='min-w-0 flex-1'>
                    <div className='text-sm font-medium leading-snug text-gray-900'>{mainText}</div>
                    {secondaryText ? <div className='mt-0.5 text-xs leading-snug text-gray-600'>{secondaryText}</div> : null}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

NewAddressAutocomplete.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onAddressSelect: PropTypes.func,
  placeholder: PropTypes.string,
  isInvalid: PropTypes.bool,
  error: PropTypes.string,
  label: PropTypes.string,
  required: PropTypes.bool,
  className: PropTypes.string,
  backfillWhenIncomplete: PropTypes.bool,
  isolateSearchInput: PropTypes.bool,
  includedPrimaryTypes: PropTypes.arrayOf(PropTypes.string),
  showSearchIcon: PropTypes.bool,
  helperText: PropTypes.string,
  allowedCountries: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.shape({ code: PropTypes.string })])),
  priorityCountries: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.shape({ code: PropTypes.string })])),
  addressParts: PropTypes.shape({
    line1: PropTypes.string,
    city: PropTypes.string,
    stateRegionProvince: PropTypes.string,
    postCode: PropTypes.string,
    country: PropTypes.string,
  }),
};

export default NewAddressAutocomplete;
